import { StagesTablePanel } from '@/features/pipelines/components/StagesTablePanel';
import { useStagesPageViewModel } from '@/features/pipelines/hooks';
import { StackedMetricsStatusChartSection } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection';
import { EntityDailyHistoryPanel } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel';
import { getDetailLabel } from '@/shared/copy/details';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { Stack } from '@/ui/layout/Stack';
import { Text } from '@/ui/primitives';
import type { ReactElement } from 'react';

export const StagesPage = (): ReactElement => {
  const {
    historyDayKeys,
    historyDateRangeLabel,
    historyHasError,
    historyLoading,
    historyNoRowsText,
    historyPage,
    historyPageSize,
    historyRows,
    expandedRowIds,
    historySortDirection,
    historySortKey,
    historyTotalCount,
    metricSectionProps,
    setHistoryPage,
    setHistoryPageSize,
    setHistorySortDirection,
    setHistorySortKey,
    setExpandedRowIds,
    tablePanelProps,
  } = useStagesPageViewModel();

  return (
    <ListPageTemplate
      headerActionsMode="separate"
      title={
        <Text role="pageTitle" component="h1">
          Stages
        </Text>
      }
      charts={<StackedMetricsStatusChartSection {...metricSectionProps} />}
      content={
        <Stack gap="2xl">
          <EntityDailyHistoryPanel
            title="Stage History"
            dateRangeLabel={historyDateRangeLabel}
            dayKeys={historyDayKeys}
            entityLabel="Stage"
            activityLabel="runs"
            rows={historyRows}
            loading={historyLoading}
            showErrorState={historyHasError}
            noRowsText={historyNoRowsText}
            expandedRowIds={expandedRowIds}
            onExpandedRowIdsChange={setExpandedRowIds}
            defaultSortKey="prefixLabel"
            sortKey={historySortKey}
            sortDirection={historySortDirection}
            sortOptions={[
              { key: 'prefixLabel', label: getDetailLabel('pipeline') },
              { key: 'name', label: getDetailLabel('stage') },
            ]}
            onSortChange={(nextSortKey, nextSortDirection) => {
              setHistorySortKey(nextSortKey);
              setHistorySortDirection(nextSortDirection);
            }}
            page={historyPage}
            pageSize={historyPageSize}
            totalCount={historyTotalCount}
            onPageChange={setHistoryPage}
            onPageSizeChange={setHistoryPageSize}
            nameColumnMinWidthPx={180}
            nameColumnDefaultWidthPx={360}
          />
          <StagesTablePanel {...tablePanelProps} />
        </Stack>
      }
    />
  );
};
